import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate} from 'react-router';
import {Concept} from 'types';
import type {NorafUpdate} from 'api/types';
import type {RowSelectionModel} from 'components/dataTable/types';
import {useLocalization} from 'localization';
import {useGetResourceWithPagination} from 'api/hooks/useGetResourceWithPagination';
import {createAgentLink, useSetAgentItems} from 'services/agentItems';
import {FlexBox, Spacer} from 'components';
import {Table} from 'schema';
import {useColumnConfigState} from 'schema/table/hooks/useColumnConfigState';
import {useSchema} from 'schemas';
import {useTableConfiguration} from 'schemas/hooks/useTableConfiguration';
import {useGetNorafUpdates} from '../hooks/useGetNorafUpdates';
import {NorafChangesSelectionActions} from './NorafChangesSelectionActions';

export const NorafChangesList = () => {
  const navigate = useNavigate();
  const {t} = useLocalization();
  const schema = useSchema(Concept.norafUpdate);
  const configuration = useTableConfiguration(Concept.norafUpdate);
  const getter = useGetNorafUpdates();

  const [selection, setSelection] = useState<RowSelectionModel>({
    type: 'include',
    ids: new Set(),
  });

  const {paginationModel, resetPaginationModel} = useColumnConfigState(
    Concept.norafUpdate,
  );

  useEffect(() => {
    resetPaginationModel();
  }, [resetPaginationModel]);

  const {
    result: {data, status},
    refresh,
  } = useGetResourceWithPagination(
    getter,
    paginationModel.pageSize,
    paginationModel.page,
  );

  const setAgentItems = useSetAgentItems();
  const handleRowClick = useCallback(
    (row: NorafUpdate) => {
      const valueAsLink = createAgentLink(row, '');
      setAgentItems([]);

      // Initiates promise, which will update redux in the background.
      getter(0, 200).then(norafUpdates => {
        const agentLinks =
          norafUpdates.items.map(norafUpdate =>
            createAgentLink(norafUpdate, t('page.agents.next.tooltip')),
          ) ?? [];

        // Remove all items before the one we clicked.
        const currentIndex = agentLinks.findIndex(
          l => l.link === valueAsLink.link,
        );

        // +1 to remove the one we navigated to.
        agentLinks.splice(0, currentIndex + 1);
        // Store in redux.
        setAgentItems(agentLinks);
      });

      // Navigate to clicked item.
      navigate(valueAsLink.link);
    },
    [getter, navigate, setAgentItems, t],
  );

  return (
    <FlexBox>
      <Spacer height={2} />
      <Table
        schema={schema}
        data={data?.items ?? []}
        configuration={configuration}
        loading={status === 'Loading'}
        header={t('page.agents.noraf.changes.title')}
        rowCount={data?.total ?? 0}
        onRowClick={handleRowClick}
        selection={selection}
        onSelectionChanged={setSelection}
      />

      <Spacer height={2} />

      <NorafChangesSelectionActions
        selection={selection}
        items={data?.items ?? []}
        postProcessing={() => {
          refresh();
        }}
        postSuccess={() => {
          setSelection({
            type: 'include',
            ids: new Set(),
          });
        }}
      />
    </FlexBox>
  );
};
